import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, attendanceAPI, leaveAPI, payrollAPI, letterAPI } from '../../../services';
import { calculateProfessionalTax } from '../../../utils/professionalTax';
import { useApi, useAsyncAction } from '../../../hooks';
import {
  Button,
  LoadingSpinner,
  Badge,
  Card,
  ErrorMessage,
  SuccessMessage,
} from '../../UI';
import { 
  ArrowLeft, 
  User, 
  Phone, 
  Mail, 
  Building2,
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Wallet,
  FileText,
  TrendingUp,
  CalendarDays,
  Shield,
  CreditCard,
  Home,
  Gift,
  IndianRupee,
  Briefcase,
  UserCircle,
  BadgeCheck,
  Edit,
  Download,
  Upload,
  Trash2,
  Eye
} from 'lucide-react';
import {ChevronDown,ChevronUp} from 'lucide-react';
import AttendanceCalendar from './AttendanceCalendar';

const EmployeeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileSubTab, setProfileSubTab] = useState('basic');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedDay, setExpandedDay] = useState(null);
  const [dailyViewMode, setDailyViewMode] = useState('calendar'); // 'calendar' or 'table'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Upload letter states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedLetters, setUploadedLetters] = useState([]);
  const [uploadingLetter, setUploadingLetter] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    letterType: 'offer',
    title: '',
    description: '',
    issueDate: new Date().toISOString().split('T')[0],
    file: null
  });

  // Letter generation hooks
  const { loading: offerLetterLoading, execute: executeOfferLetter } = useAsyncAction();
  const { loading: appointmentLetterLoading, execute: executeAppointmentLetter } = useAsyncAction();
  const { loading: confirmationLetterLoading, execute: executeConfirmationLetter } = useAsyncAction();
  const { loading: experienceLetterLoading, execute: executeExperienceLetter } = useAsyncAction();

  // Personal details approval states
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Fetch employee details
  const { data: employeeData, loading: employeeLoading, refetch: refetchEmployee } = useApi(
    () => userAPI.getUser(id),
    [id]
  );

  // Fetch attendance for selected date
  const { data: dailyAttendance, loading: dailyLoading, refetch: refetchDaily } = useApi(
    () => attendanceAPI.getEmployeesAttendance({ employeeId: id, date: selectedDate }),
    [id, selectedDate]
  );

  // Fetch monthly attendance - get date range for month
  const { data: monthlyAttendance, loading: monthlyLoading, refetch: refetchMonthly } = useApi(
    () => {
      // Create start and end dates in YYYY-MM-DD format without timezone issues
      const startDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      const endDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      console.log('📅 Monthly attendance API params:', { 
        employeeId: id, 
        startDate, 
        endDate,
        selectedMonth,
        selectedYear,
        lastDay
      });
      return attendanceAPI.getEmployeesAttendance({ 
        employeeId: id, 
        startDate, 
        endDate,
        limit: 50
      });
    },
    [id, selectedMonth, selectedYear]
  );

  // Fetch leaves
  const { data: leavesData, loading: leavesLoading } = useApi(
    () => leaveAPI.getEmployeeLeaves(id, { year: selectedYear }),
    [id, selectedYear]
  );

  // Fetch payslips
  const { data: payrollData, loading: payrollLoading } = useApi(
    () => payrollAPI.getPayslips({ employeeId: id, year: selectedYear }),
    [id, selectedYear]
  );

  const employee = employeeData?.user;
  // Note: useApi hook accesses response.data, so we access properties directly
  const dailyRecords = dailyAttendance?.attendance || [];
  const attendance = dailyRecords.length > 0 ? dailyRecords[0] : null;
  const monthlyRecords = monthlyAttendance?.attendance || [];
  const leaves = leavesData?.leaves || leavesData || [];
  const payslips = Array.isArray(payrollData) ? payrollData : (payrollData?.payslips || []);

  // Fetch uploaded letters
  const fetchUploadedLetters = async () => {
    try {
      const response = await letterAPI.getUploadedLetters(id);
      if (response.data) {
        setUploadedLetters(response.data);
      }
    } catch (err) {
      console.error('Error fetching uploaded letters:', err);
    }
  };

  // Fetch uploaded letters when letters sub-tab is active
  useEffect(() => {
    if (profileSubTab === 'letters' && id) {
      fetchUploadedLetters();
    }
  }, [profileSubTab, id]);

  // Debug logging for monthly attendance
  console.log('📋 Monthly attendance data:', { 
    monthlyAttendance,
    monthlyRecords,
    selectedMonth,
    selectedYear
  });

  // Debug logging for failed attempts
  console.log('📋 Daily attendance data:', { 
    dailyRecords, 
    hasAttendance: !!attendance,
    failedAttempts: dailyRecords[0]?.failedAttempts,
    punchIn: dailyRecords[0]?.punchIn
  });

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const goToPrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Format work duration - handles 0 and small values
  const formatWorkDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  const getStatusColor = (status) => {
    const colors = {
      present: 'bg-green-100 text-green-700',
      absent: 'bg-red-100 text-red-700',
      half_day: 'bg-yellow-100 text-yellow-700',
      leave: 'bg-blue-100 text-blue-700',
      holiday: 'bg-purple-100 text-purple-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getLeaveStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (employeeLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Employee not found</h3>
        <Button onClick={() => navigate('/dashboard/employees')} className="mt-4">
          Back to Employees
        </Button>
      </div>
    );
  }

  // Letter download handlers
  const handleOfferLetter = async () => {
    try {
      await executeOfferLetter(() => letterAPI.getOfferLetter(id));
      setSuccess('Offer letter downloaded successfully');
    } catch (err) {
      setError(err.message || 'Failed to generate offer letter');
    }
  };

  const handleAppointmentLetter = async () => {
    try {
      await executeAppointmentLetter(() => letterAPI.getAppointmentLetter(id));
      setSuccess('Appointment letter downloaded successfully');
    } catch (err) {
      setError(err.message || 'Failed to generate appointment letter');
    }
  };

  const handleConfirmationLetter = async () => {
    try {
      await executeConfirmationLetter(() => letterAPI.getConfirmationLetter(id));
      setSuccess('Confirmation letter downloaded successfully');
    } catch (err) {
      setError(err.message || 'Failed to generate confirmation letter');
    }
  };

  const handleExperienceLetter = async () => {
    // Only allow generation if employee is deactivated and has a last working date
    if (employee.active || !employee.inactivation?.date) {
      setError('Experience letter can only be generated for deactivated employees with a last working date');
      return;
    }
    try {
      // Pass the inactivation date as lastWorkingDate
      const lastWorkingDate = new Date(employee.inactivation.date).toISOString().split('T')[0];
      await executeExperienceLetter(() => letterAPI.getExperienceLetter(id, lastWorkingDate));
      setSuccess('Experience letter downloaded successfully');
    } catch (err) {
      setError(err.message || 'Failed to generate experience letter');
    }
  };

  // Handle letter upload
  const handleUploadLetter = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      setError('Please select a PDF file to upload');
      return;
    }

    setUploadingLetter(true);
    try {
      const formData = new FormData();
      formData.append('letter', uploadForm.file);
      formData.append('letterType', uploadForm.letterType);
      formData.append('title', uploadForm.title || uploadForm.file.name);
      formData.append('description', uploadForm.description);
      formData.append('issueDate', uploadForm.issueDate);

      const response = await letterAPI.uploadLetter(id, formData);
      if (response.data) {
        setSuccess('Letter uploaded successfully');
        setShowUploadModal(false);
        setUploadForm({
          letterType: 'offer',
          title: '',
          description: '',
          issueDate: new Date().toISOString().split('T')[0],
          file: null
        });
        fetchUploadedLetters();
      }
    } catch (err) {
      setError(err.message || 'Failed to upload letter');
    } finally {
      setUploadingLetter(false);
    }
  };

  // Handle delete uploaded letter
  const handleDeleteUploadedLetter = async (letterId) => {
    if (!window.confirm('Are you sure you want to delete this letter?')) return;

    try {
      await letterAPI.deleteUploadedLetter(letterId);
      setSuccess('Letter deleted successfully');
      fetchUploadedLetters();
    } catch (err) {
      setError(err.message || 'Failed to delete letter');
    }
  };

  // Letter type options
  const letterTypeOptions = [
    { value: 'offer', label: 'Offer Letter' },
    { value: 'appointment', label: 'Appointment Letter' },
    { value: 'confirmation', label: 'Confirmation Letter' },
    { value: 'experience', label: 'Experience Letter' },
    { value: 'increment', label: 'Increment Letter' },
    { value: 'promotion', label: 'Promotion Letter' },
    { value: 'warning', label: 'Warning Letter' },
    { value: 'termination', label: 'Termination Letter' },
    { value: 'relieving', label: 'Relieving Letter' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Back Button & Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/dashboard/employees')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Employee Details</h1>
          <p className="text-gray-500">View and track employee information</p>
        </div>
      </div>

      {/* Employee Info Card */}
      <Card className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
              {employee.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{employee.name}</h2>
              <p className="text-gray-500">{employee.department || 'No Department'}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`${employee.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                  {employee.role}
                </Badge>
                {/* Location Type Badge */}
                <Badge className={`${
                  employee.locationType === 'field' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {employee.locationType === 'field' ? '📍 Field' : '🏢 Office'}
                </Badge>
              </div>
            </div>
          </div>
          
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{employee.email || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{employee.phone || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{employee.client?.name || 'N/A'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Joined: {formatDate(employee.createdAt)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'profile', label: 'Profile', icon: UserCircle },
          { id: 'attendance', label: 'Attendance', icon: Clock },
          { id: 'daily', label: 'Daily Log', icon: CalendarDays },
          // Show Travel Map tab only for field employees
          ...(employee.locationType === 'field' ? [{ id: 'travelmap', label: 'Travel Map', icon: MapPin }] : []),
          { id: 'leaves', label: 'Leaves', icon: FileText },
          { id: 'payroll', label: 'Payroll', icon: Wallet }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      
      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          {/* Profile Sub-Tabs */}
          <div className="bg-white rounded-xl border border-gray-200 p-1 inline-flex gap-1">
            {[
              { id: 'basic', label: 'Basic Info', icon: User },
              { id: 'salary', label: 'Salary & Statutory', icon: IndianRupee },
              { id: 'personal', label: 'Personal', icon: Home },
              { id: 'benefits', label: 'Benefits & Payment', icon: Gift },
              { id: 'letters', label: 'Letters', icon: FileText }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setProfileSubTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  profileSubTab === tab.id
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Edit Button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/dashboard/employees/${id}/edit`)}
              className="flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit Employee
            </Button>
          </div>

          {/* Basic Info Sub-Tab */}
          {profileSubTab === 'basic' && (
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="text-sm text-gray-500">Full Name</label>
                  <p className="font-medium text-gray-900">{employee.name || `${employee.firstName || ''} ${employee.lastName || ''}`.trim() || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Employee ID</label>
                  <p className="font-medium text-gray-900">{employee.employeeId || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Username</label>
                  <p className="font-medium text-gray-900">{employee.username || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Work Email</label>
                  <p className="font-medium text-gray-900">{employee.email || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Phone</label>
                  <p className="font-medium text-gray-900">{employee.phone || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Gender</label>
                  <p className="font-medium text-gray-900 capitalize">{employee.gender || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Date of Joining</label>
                  <p className="font-medium text-gray-900">{formatDate(employee.dateOfJoining) || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Department</label>
                  <p className="font-medium text-gray-900">{employee.department || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Designation</label>
                  <p className="font-medium text-gray-900">{employee.designation || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Employee Type</label>
                  <p className="font-medium text-gray-900 capitalize">{employee.employeeType?.replace('_', ' ') || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Reporting To</label>
                  <p className="font-medium text-gray-900">
                    {employee.reportingTo?.name || '-'}
                    {employee.reportingTo?.designation && (
                      <span className="text-sm text-gray-500"> ({employee.reportingTo.designation})</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Role</label>
                  <Badge className={`${employee.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {employee.role}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Location Type</label>
                  <Badge className={`${employee.locationType === 'field' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {employee.locationType === 'field' ? '📍 Field' : '🏢 Office'}
                  </Badge>
                </div>
                {employee.shift && (
                  <div>
                    <label className="text-sm text-gray-500">Work Shift</label>
                    <p className="font-medium text-gray-900">
                      {employee.shift.name}
                      {employee.shift.isNightShift && <span className="ml-1 text-xs text-purple-600">🌙 Night</span>}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(() => {
                        const fmt = (t) => { const [h,m] = t.split(':').map(Number); return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`; };
                        return `${fmt(employee.shift.startTime)} – ${fmt(employee.shift.endTime)}`;
                      })()}
                    </p>
                  </div>
                )}
              </div>

              {/* Probation / Internship Details */}
              {employee.probation?.status && employee.probation.status !== '' && (
                <div className={`mt-4 rounded-lg p-4 ${
                  employee.probation.status === 'confirmed' ? 'bg-green-50 border border-green-200' :
                  employee.probation.status === 'terminated' ? 'bg-red-50 border border-red-200' :
                  'bg-yellow-50 border border-yellow-200'
                }`}>
                  <h4 className={`text-sm font-semibold mb-2 ${
                    employee.probation.status === 'confirmed' ? 'text-green-800' :
                    employee.probation.status === 'terminated' ? 'text-red-800' :
                    'text-yellow-800'
                  }`}>
                    Probation / Internship Details
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-gray-500">Status</label>
                      <p className="text-sm font-medium capitalize">{employee.probation.status}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Duration</label>
                      <p className="text-sm font-medium">{employee.probation.durationDays} days</p>
                    </div>
                    {employee.probation.startDate && (
                      <div>
                        <label className="text-xs text-gray-500">Start Date</label>
                        <p className="text-sm font-medium">{new Date(employee.probation.startDate).toLocaleDateString('en-IN')}</p>
                      </div>
                    )}
                    {employee.probation.endDate && (
                      <div>
                        <label className="text-xs text-gray-500">End Date</label>
                        <p className="text-sm font-medium">{new Date(employee.probation.endDate).toLocaleDateString('en-IN')}</p>
                      </div>
                    )}
                    {employee.probation.extendedCount > 0 && (
                      <div>
                        <label className="text-xs text-gray-500">Extended</label>
                        <p className="text-sm font-medium">{employee.probation.extendedCount} time(s) (+{employee.probation.totalExtendedDays} days)</p>
                      </div>
                    )}
                    {employee.probation.confirmedDate && (
                      <div>
                        <label className="text-xs text-gray-500">Confirmed On</label>
                        <p className="text-sm font-medium">{new Date(employee.probation.confirmedDate).toLocaleDateString('en-IN')}</p>
                      </div>
                    )}
                  </div>
                  {employee.probation.notes && (
                    <p className="text-xs text-gray-600 mt-2 italic">{employee.probation.notes}</p>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Salary & Statutory Sub-Tab */}
          {profileSubTab === 'salary' && (
            <div className="space-y-6">
              {/* CTC Summary */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <p className="text-green-100 text-sm">Annual CTC</p>
                    <p className="text-3xl font-bold">₹{(employee.salary?.annualCTC || 0).toLocaleString('en-IN')}</p>
                  </div>
                  <div>
                    <p className="text-green-100 text-sm">Monthly CTC</p>
                    <p className="text-2xl font-bold">₹{((employee.salary?.annualCTC || 0) / 12).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  </div>
                  <div>
                    <p className="text-green-100 text-sm">Daily Rate</p>
                    <p className="text-2xl font-bold">₹{((employee.salary?.annualCTC || 0) / 365).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                  </div>
                </div>
              </div>

              {/* Salary Components */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <IndianRupee className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Salary Components</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">Basic Salary</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Percentage:</span>
                        <span className="font-medium text-blue-900">{employee.salary?.components?.basic?.value || 0}% of CTC</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Monthly:</span>
                        <span className="font-medium text-blue-900">₹{(employee.salary?.components?.basic?.monthly || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Annual:</span>
                        <span className="font-medium text-blue-900">₹{(employee.salary?.components?.basic?.annual || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <h4 className="font-medium text-purple-900 mb-2">House Rent Allowance</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-purple-700">Percentage:</span>
                        <span className="font-medium text-purple-900">{employee.salary?.components?.hra?.value || 0}% of Basic</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-700">Monthly:</span>
                        <span className="font-medium text-purple-900">₹{(employee.salary?.components?.hra?.monthly || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-700">Annual:</span>
                        <span className="font-medium text-purple-900">₹{(employee.salary?.components?.hra?.annual || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                      </div>
                    </div>
                  </div>

                  {employee.salary?.components?.conveyance?.enabled && (
                    <div className="p-4 bg-teal-50 rounded-xl border border-teal-200">
                      <h4 className="font-medium text-teal-900 mb-2">Conveyance Allowance</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-teal-700">Type:</span>
                          <span className="font-medium text-teal-900">Fixed</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-teal-700">Monthly:</span>
                          <span className="font-medium text-teal-900">₹{(employee.salary?.components?.conveyance?.monthly || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-teal-700">Annual:</span>
                          <span className="font-medium text-teal-900">₹{(employee.salary?.components?.conveyance?.annual || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {(employee.salary?.components?.customAllowances || []).map((allowance, index) => (
                    <div key={index} className="p-4 bg-indigo-50 rounded-xl border border-indigo-200">
                      <h4 className="font-medium text-indigo-900 mb-2">{allowance.name || 'Custom Allowance'}</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-indigo-700">Type:</span>
                          <span className="font-medium text-indigo-900">Fixed</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-indigo-700">Monthly:</span>
                          <span className="font-medium text-indigo-900">₹{(allowance.monthly || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-indigo-700">Annual:</span>
                          <span className="font-medium text-indigo-900">₹{(allowance.annual || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Legacy: new salary structures put the remainder in Conveyance, leaving this at 0 */}
                  {(employee.salary?.components?.fixedAllowance?.monthly || 0) > 0 && (
                    <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                      <h4 className="font-medium text-orange-900 mb-2">Fixed Allowance</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-orange-700">Type:</span>
                          <span className="font-medium text-orange-900">System Calculated</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-orange-700">Monthly:</span>
                          <span className="font-medium text-orange-900">₹{(employee.salary?.components?.fixedAllowance?.monthly || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-orange-700">Annual:</span>
                          <span className="font-medium text-orange-900">₹{(employee.salary?.components?.fixedAllowance?.annual || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* EPF Employer Contribution */}
                {employee.salary?.epfEmployer?.enabled && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-3">EPF - Employer Contribution</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-700">Rate</p>
                        <p className="font-medium text-green-900">{employee.salary?.epfEmployer?.percentage || 12}% of PF Wages</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-700">Monthly</p>
                        <p className="font-medium text-green-900">₹{(employee.salary?.epfEmployer?.monthly || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-700">Annual</p>
                        <p className="font-medium text-green-900">₹{(employee.salary?.epfEmployer?.annual || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                      </div>
                    </div>
                    {employee.salary?.epfEmployer?.contributeToPension && (
                      <p className="mt-2 text-sm text-gray-600">✓ Contributing to Employee Pension Scheme</p>
                    )}
                  </div>
                )}
              </Card>

              {/* Statutory Components */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Statutory Components</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* EPF */}
                  <div className={`p-4 rounded-xl border-2 ${employee.statutory?.epfEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">EPF</h4>
                      <Badge className={employee.statutory?.epfEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                        {employee.statutory?.epfEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    {employee.statutory?.epfEnabled && (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Account:</span>
                          <span className="font-medium">{employee.statutory?.epf?.accountNumber || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">UAN:</span>
                          <span className="font-medium">{employee.statutory?.epf?.uan || '-'}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ESI */}
                  <div className={`p-4 rounded-xl border-2 ${employee.statutory?.esiEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">ESI</h4>
                      <Badge className={employee.statutory?.esiEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                        {employee.statutory?.esiEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    {employee.statutory?.esiEnabled && (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Insurance No:</span>
                          <span className="font-medium">{employee.statutory?.esi?.insuranceNumber || '-'}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* PT */}
                  <div className={`p-4 rounded-xl border-2 ${employee.statutory?.ptEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">Professional Tax</h4>
                      <Badge className={employee.statutory?.ptEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                        {employee.statutory?.ptEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                    {employee.statutory?.ptEnabled && (() => {
                      const ptState = employee.statutory?.pt?.state || '';
                      const ptResult = calculateProfessionalTax({
                        state: ptState,
                        monthlyGross: (employee.salary?.annualCTC || 0) / 12,
                        gender: employee.gender
                      });
                      return (
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">State:</span>
                            <span className="font-medium">
                              {ptResult.stateName || (ptState ? ptState.replace(/_/g, ' ') : 'Company default')}
                            </span>
                          </div>
                          {ptResult.applicable ? (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Filing:</span>
                                <span className="font-medium">{ptResult.frequencyLabel}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Per month:</span>
                                <span className="font-medium">₹{ptResult.baseMonthlyAmount.toLocaleString('en-IN')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Per year:</span>
                                <span className="font-medium">₹{ptResult.annualAmount.toLocaleString('en-IN')}</span>
                              </div>
                            </>
                          ) : (
                            <p className="text-xs text-gray-500">{ptResult.reason}</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* Personal Sub-Tab */}
          {profileSubTab === 'personal' && (
            <div className="space-y-6">
            {/* Pending Personal Details Approval */}
            {employee?.pendingPersonal?.status === 'pending' && (
              <Card className="p-6 border-2 border-amber-300 bg-amber-50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-200 flex items-center justify-center">
                      <AlertCircle className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-amber-800">Pending Personal Details</h3>
                      <p className="text-sm text-amber-600">Employee submitted personal details for approval on {new Date(employee.pendingPersonal.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        try {
                          setApprovalLoading(true);
                          await userAPI.approvePersonalDetails(id);
                          setSuccess('Personal details approved successfully');
                          refetchEmployee();
                        } catch (err) {
                          setError(err.response?.data?.message || 'Failed to approve');
                        } finally {
                          setApprovalLoading(false);
                        }
                      }}
                      disabled={approvalLoading}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => setShowRejectModal(true)}
                      disabled={approvalLoading}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Reject
                    </Button>
                  </div>
                </div>

                {/* Submitted Data */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                  {employee.pendingPersonal.data?.dateOfBirth && (
                    <div className="bg-white p-3 rounded-lg">
                      <label className="text-xs text-gray-500">Date of Birth</label>
                      <p className="font-medium text-gray-900">{new Date(employee.pendingPersonal.data.dateOfBirth).toLocaleDateString('en-IN')}</p>
                    </div>
                  )}
                  {employee.pendingPersonal.data?.age && (
                    <div className="bg-white p-3 rounded-lg">
                      <label className="text-xs text-gray-500">Age</label>
                      <p className="font-medium text-gray-900">{employee.pendingPersonal.data.age} years</p>
                    </div>
                  )}
                  {employee.pendingPersonal.data?.fatherName && (
                    <div className="bg-white p-3 rounded-lg">
                      <label className="text-xs text-gray-500">Father's Name</label>
                      <p className="font-medium text-gray-900">{employee.pendingPersonal.data.fatherName}</p>
                    </div>
                  )}
                  {employee.pendingPersonal.data?.pan && (
                    <div className="bg-white p-3 rounded-lg">
                      <label className="text-xs text-gray-500">PAN Number</label>
                      <p className="font-medium text-gray-900">{employee.pendingPersonal.data.pan}</p>
                    </div>
                  )}
                  {employee.pendingPersonal.data?.personalEmail && (
                    <div className="bg-white p-3 rounded-lg">
                      <label className="text-xs text-gray-500">Personal Email</label>
                      <p className="font-medium text-gray-900">{employee.pendingPersonal.data.personalEmail}</p>
                    </div>
                  )}
                  {employee.pendingPersonal.data?.differentlyAbled && (
                    <div className="bg-white p-3 rounded-lg">
                      <label className="text-xs text-gray-500">Differently Abled</label>
                      <p className="font-medium text-gray-900 capitalize">{employee.pendingPersonal.data.differentlyAbled}</p>
                    </div>
                  )}
                </div>
                {/* Submitted Address */}
                {(employee.pendingPersonal.data?.address?.line1 || employee.pendingPersonal.data?.address?.city) && (
                  <div className="mt-4 pt-4 border-t border-amber-200">
                    <h4 className="text-sm font-medium text-amber-800 mb-3 flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5" /> Submitted Address
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {employee.pendingPersonal.data.address?.line1 && (
                        <div className="bg-white p-3 rounded-lg">
                          <label className="text-xs text-gray-500">Address Line 1</label>
                          <p className="font-medium text-gray-900">{employee.pendingPersonal.data.address.line1}</p>
                        </div>
                      )}
                      {employee.pendingPersonal.data.address?.line2 && (
                        <div className="bg-white p-3 rounded-lg">
                          <label className="text-xs text-gray-500">Address Line 2</label>
                          <p className="font-medium text-gray-900">{employee.pendingPersonal.data.address.line2}</p>
                        </div>
                      )}
                      {employee.pendingPersonal.data.address?.city && (
                        <div className="bg-white p-3 rounded-lg">
                          <label className="text-xs text-gray-500">City</label>
                          <p className="font-medium text-gray-900">{employee.pendingPersonal.data.address.city}</p>
                        </div>
                      )}
                      {employee.pendingPersonal.data.address?.state && (
                        <div className="bg-white p-3 rounded-lg">
                          <label className="text-xs text-gray-500">State</label>
                          <p className="font-medium text-gray-900">{employee.pendingPersonal.data.address.state}</p>
                        </div>
                      )}
                      {employee.pendingPersonal.data.address?.pincode && (
                        <div className="bg-white p-3 rounded-lg">
                          <label className="text-xs text-gray-500">Pincode</label>
                          <p className="font-medium text-gray-900">{employee.pendingPersonal.data.address.pincode}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Reject Modal */}
                {showRejectModal && (
                  <div className="mt-4 p-4 bg-white rounded-lg border border-red-200">
                    <h4 className="text-sm font-semibold text-red-700 mb-2">Rejection Reason</h4>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      rows={3}
                    />
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={async () => {
                          try {
                            setApprovalLoading(true);
                            await userAPI.rejectPersonalDetails(id, rejectReason);
                            setSuccess('Personal details rejected');
                            setShowRejectModal(false);
                            setRejectReason('');
                            refetchEmployee();
                          } catch (err) {
                            setError(err.response?.data?.message || 'Failed to reject');
                          } finally {
                            setApprovalLoading(false);
                          }
                        }}
                        disabled={approvalLoading || !rejectReason.trim()}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        Confirm Reject
                      </Button>
                      <Button
                        onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}

            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Home className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Personal Details</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className="text-sm text-gray-500">Date of Birth</label>
                  <p className="font-medium text-gray-900">{formatDate(employee.personal?.dateOfBirth) || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Age</label>
                  <p className="font-medium text-gray-900">{employee.personal?.age ? `${employee.personal.age} years` : '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Father's Name</label>
                  <p className="font-medium text-gray-900">{employee.personal?.fatherName || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">PAN Number</label>
                  <p className="font-medium text-gray-900">{employee.personal?.pan || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Personal Email</label>
                  <p className="font-medium text-gray-900">{employee.personal?.personalEmail || '-'}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-500">Differently Abled</label>
                  <p className="font-medium text-gray-900 capitalize">{employee.personal?.differentlyAbled || 'No'}</p>
                </div>
              </div>
              
              {/* Address */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  Address
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="text-sm text-gray-500">Address Line 1</label>
                    <p className="font-medium text-gray-900">{employee.personal?.address?.line1 || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Address Line 2</label>
                    <p className="font-medium text-gray-900">{employee.personal?.address?.line2 || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">City</label>
                    <p className="font-medium text-gray-900">{employee.personal?.address?.city || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">State</label>
                    <p className="font-medium text-gray-900">{employee.personal?.address?.state || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500">Pincode</label>
                    <p className="font-medium text-gray-900">{employee.personal?.address?.pincode || '-'}</p>
                  </div>
                </div>
              </div>
            </Card>
            </div>
          )}

          {/* Benefits & Payment Sub-Tab */}
          {profileSubTab === 'benefits' && (
            <div className="space-y-6">
              {/* Benefits */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-pink-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Benefits</h3>
                  <Badge className={employee.benefits?.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                    {employee.benefits?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                
                {employee.benefits?.enabled && employee.benefits?.items?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {employee.benefits.items.map((benefit, index) => (
                      <div key={index} className="p-4 bg-pink-50 rounded-xl border border-pink-200 flex items-center gap-3">
                        <BadgeCheck className="w-5 h-5 text-pink-600" />
                        <div>
                          <p className="font-medium text-pink-900">{benefit.name || benefit.id}</p>
                          {benefit.amount > 0 && (
                            <p className="text-sm text-pink-700">₹{benefit.amount} / {benefit.frequency}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Gift className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No benefits configured for this employee</p>
                  </div>
                )}
              </Card>

              {/* Payment Method */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-cyan-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Payment Method</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-cyan-50 rounded-xl border border-cyan-200">
                    <h4 className="font-medium text-cyan-900 mb-3">Payment Type</h4>
                    <Badge className="bg-cyan-100 text-cyan-700 capitalize">
                      {employee.payment?.method?.replace('_', ' ') || 'Bank Transfer'}
                    </Badge>
                  </div>
                  
                  {employee.payment?.method === 'bank_transfer' && (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-3">Bank Details</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Bank Name:</span>
                          <span className="font-medium">{employee.payment?.bankDetails?.bankName || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Branch:</span>
                          <span className="font-medium">{employee.payment?.bankDetails?.branchName || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Account Number:</span>
                          <span className="font-medium">{employee.payment?.bankDetails?.accountNumber || '-'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">IFSC Code:</span>
                          <span className="font-medium">{employee.payment?.bankDetails?.ifscCode || '-'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* Letters Sub-Tab */}
          {profileSubTab === 'letters' && (
            <div className="space-y-6">
              {/* Generate Letters Card */}
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Generate Letters</h3>
                    <p className="text-sm text-gray-500">Auto-generate official letters for this employee</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Offer Letter */}
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-blue-900">Offer Letter</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Formal job offer with compensation details
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <Button
                      onClick={handleOfferLetter}
                      loading={offerLetterLoading}
                      className="mt-4 w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Offer Letter
                    </Button>
                  </div>

                  {/* Appointment Letter */}
                  <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-green-900">Appointment Letter</h4>
                        <p className="text-sm text-green-700 mt-1">
                          Official appointment with terms and conditions
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <Button
                      onClick={handleAppointmentLetter}
                      loading={appointmentLetterLoading}
                      className="mt-4 w-full bg-green-600 hover:bg-green-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Appointment Letter
                    </Button>
                  </div>

                  {/* Confirmation Letter */}
                  <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-amber-900">Confirmation Letter</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          Confirmation after successful probation period
                        </p>
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center">
                        <BadgeCheck className="w-5 h-5 text-white" />
                      </div>
                    </div>
                    <Button
                      onClick={handleConfirmationLetter}
                      loading={confirmationLetterLoading}
                      className="mt-4 w-full bg-amber-600 hover:bg-amber-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Confirmation Letter
                    </Button>
                  </div>

                  {/* Experience Letter - Only show for deactivated employees */}
                  {!employee.active && employee.inactivation?.date ? (
                    <div className="p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-xl border border-red-200">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-red-900">Experience Letter</h4>
                          <p className="text-sm text-red-700 mt-1">
                            Last Working Date: {new Date(employee.inactivation.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-red-500 flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <Button
                        onClick={handleExperienceLetter}
                        loading={experienceLetterLoading}
                        className="mt-4 w-full bg-red-600 hover:bg-red-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Experience Letter
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 opacity-60">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-600">Experience Letter</h4>
                          <p className="text-sm text-gray-500 mt-1">
                            Available only after employee deactivation
                          </p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-gray-400 flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <Button
                        disabled
                        className="mt-4 w-full bg-gray-400 cursor-not-allowed"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Experience Letter
                      </Button>
                    </div>
                  )}
                </div>
              </Card>

              {/* Upload Custom Letters Card */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Upload className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Uploaded Letters</h3>
                      <p className="text-sm text-gray-500">Upload custom letters for this employee</p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => setShowUploadModal(true)}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Letter
                  </Button>
                </div>

                {uploadedLetters.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No uploaded letters yet</p>
                    <p className="text-sm">Click "Upload Letter" to add custom letters for this employee</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {uploadedLetters.map((letter) => (
                      <div 
                        key={letter._id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{letter.title}</h4>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Badge variant="secondary" className="text-xs">
                                {letterTypeOptions.find(t => t.value === letter.letterType)?.label || letter.letterType}
                              </Badge>
                              <span>•</span>
                              <span>{new Date(letter.issueDate).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(letter.fileUrl, '_blank')}
                            title="View"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => letterAPI.downloadUploadedLetter(letter._id)}
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteUploadedLetter(letter._id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              {/* Upload Modal */}
              {showUploadModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Letter</h3>
                    <form onSubmit={handleUploadLetter} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Letter Type</label>
                        <select
                          value={uploadForm.letterType}
                          onChange={(e) => setUploadForm({ ...uploadForm, letterType: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          {letterTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                          type="text"
                          value={uploadForm.title}
                          onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                          placeholder="Enter letter title"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                        <textarea
                          value={uploadForm.description}
                          onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                          placeholder="Enter description"
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                        <input
                          type="date"
                          value={uploadForm.issueDate}
                          onChange={(e) => setUploadForm({ ...uploadForm, issueDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PDF File</label>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                        {uploadForm.file && (
                          <p className="text-sm text-gray-500 mt-1">Selected: {uploadForm.file.name}</p>
                        )}
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setShowUploadModal(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          loading={uploadingLetter}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          Upload Letter
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'attendance' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Monthly Attendance</h3>
            <div className="flex items-center gap-2">
              <button onClick={goToPrevMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="font-medium text-gray-900 min-w-[150px] text-center">
                {months[selectedMonth - 1]} {selectedYear}
              </span>
              <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {monthlyLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : (
            <>
              {/* Monthly Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {monthlyRecords.filter(a => a.status === 'present').length}
                  </p>
                  <p className="text-sm text-green-700">Present</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-600">
                    {monthlyRecords.filter(a => a.status === 'absent').length}
                  </p>
                  <p className="text-sm text-red-700">Absent</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-yellow-600">
                    {monthlyRecords.filter(a => a.status === 'half_day').length}
                  </p>
                  <p className="text-sm text-yellow-700">Half Day</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">
                    {monthlyRecords.filter(a => a.status === 'leave').length}
                  </p>
                  <p className="text-sm text-blue-700">Leave</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-indigo-600">
                    {Math.floor(monthlyRecords.reduce((sum, a) => sum + (a.workDuration || 0), 0) / 60)}h
                  </p>
                  <p className="text-sm text-indigo-700">Total Hours</p>
                </div>
              </div>

              {/* Attendance Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Punch In</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Punch Out</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Duration</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Late</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {monthlyRecords.length > 0 ? monthlyRecords.map((record, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm">{formatDate(record.date)}</td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusColor(record.status)}>
                            {record.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">{formatTime(record.punchIn?.time)}</td>
                        <td className="py-3 px-4 text-sm">{formatTime(record.punchOut?.time)}</td>
                        <td className="py-3 px-4 text-sm">
                          {formatWorkDuration(record.workDuration)}
                        </td>
                        <td className="py-3 px-4">
                          {record.isLateArrival ? (
                            <AlertCircle className="w-4 h-4 text-orange-500" />
                          ) : record.punchIn?.time ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : '-'}
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="py-8 text-center text-gray-500">
                          No attendance records for this month
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}

      {activeTab === 'daily' && (
        <Card className="p-6">
          {/* Header with Month/Year Selector */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h3 className="font-semibold text-gray-900">Monthly Attendance Log</h3>
              <p className="text-sm text-gray-500">
                {monthlyRecords.length} records for {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (selectedMonth === 1) {
                    setSelectedMonth(12);
                    setSelectedYear(selectedYear - 1);
                  } else {
                    setSelectedMonth(selectedMonth - 1);
                  }
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2024, i).toLocaleDateString('en-IN', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                {[2024, 2025, 2026, 2027].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <button
                onClick={() => {
                  if (selectedMonth === 12) {
                    setSelectedMonth(1);
                    setSelectedYear(selectedYear + 1);
                  } else {
                    setSelectedMonth(selectedMonth + 1);
                  }
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>

              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5 ml-2">
                <button
                  onClick={() => setDailyViewMode('calendar')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    dailyViewMode === 'calendar'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <CalendarDays className="w-4 h-4 inline mr-1" />
                  Calendar
                </button>
                <button
                  onClick={() => setDailyViewMode('table')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    dailyViewMode === 'table'
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <TrendingUp className="w-4 h-4 inline mr-1" />
                  Table
                </button>
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{monthlyRecords.filter(r => r.status === 'present').length}</p>
              <p className="text-xs text-green-600">Present</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-700">
                {(() => {
                  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
                  const today = new Date();
                  const currentMonthDay = selectedYear === today.getFullYear() && selectedMonth === today.getMonth() + 1 ? today.getDate() : daysInMonth;
                  const workingDays = Array.from({ length: currentMonthDay }, (_, i) => {
                    const day = new Date(selectedYear, selectedMonth - 1, i + 1).getDay();
                    return day !== 0; // Exclude Sundays
                  }).filter(Boolean).length;
                  return Math.max(0, workingDays - monthlyRecords.filter(r => r.status === 'present' || r.status === 'half_day' || r.status === 'leave').length);
                })()}
              </p>
              <p className="text-xs text-red-600">Absent</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-yellow-700">{monthlyRecords.filter(r => r.status === 'half_day').length}</p>
              <p className="text-xs text-yellow-600">Half Day</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-purple-700">{monthlyRecords.filter(r => r.status === 'leave').length}</p>
              <p className="text-xs text-purple-600">Leave</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-orange-700">{monthlyRecords.filter(r => r.isLateArrival).length}</p>
              <p className="text-xs text-orange-600">Late</p>
            </div>
            <div className="bg-pink-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-pink-700">
                {monthlyRecords.reduce((sum, r) => sum + (r.locationLogs?.filter(l => l.isMissed)?.length || 0), 0)}
              </p>
              <p className="text-xs text-pink-600">Missed Checks</p>
            </div>
            <div className="bg-rose-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-rose-700">
                {monthlyRecords.reduce((sum, r) => sum + (r.failedAttempts?.length || 0), 0)}
              </p>
              <p className="text-xs text-rose-600">Failed Punches</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">
                {Math.round(monthlyRecords.reduce((sum, r) => sum + (r.workDuration || 0), 0) / 60)}h
              </p>
              <p className="text-xs text-blue-600">Total Hours</p>
            </div>
          </div>

          {monthlyLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : monthlyRecords.length > 0 ? (
            <>
              {/* Calendar View */}
              {dailyViewMode === 'calendar' && (
                <AttendanceCalendar
                  monthlyRecords={monthlyRecords}
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                />
              )}

              {/* Table View */}
              {dailyViewMode === 'table' && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Date</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700">Status</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700 hidden sm:table-cell">Punch In</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700 hidden sm:table-cell">Punch Out</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700 hidden md:table-cell">Hours</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700 hidden lg:table-cell">Checks</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-700 hidden lg:table-cell">Failed</th>
                    <th className="px-3 py-3 text-center font-medium text-gray-700 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {monthlyRecords.sort((a, b) => new Date(b.date) - new Date(a.date)).map((record, idx) => {
                    const recordDate = new Date(record.date);
                    const isExpanded = expandedDay === record._id;
                    
                    return (
                      <React.Fragment key={record._id || idx}>
                        <tr 
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50' : ''}`}
                          onClick={() => setExpandedDay(isExpanded ? null : record._id)}
                        >
                          <td className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-gray-100 flex flex-col items-center justify-center text-xs">
                                <span className="font-bold text-gray-800">{recordDate.getDate()}</span>
                                <span className="text-gray-500 text-[10px] uppercase">{recordDate.toLocaleDateString('en-IN', { weekday: 'short' })}</span>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <Badge className={`text-xs ${getStatusColor(record.status)}`}>
                              {record.status?.replace('_', ' ')}
                            </Badge>
                            {record.isLateArrival && (
                              <span className="ml-1 text-xs text-orange-500">⏰</span>
                            )}
                          </td>
                          <td className="px-3 py-3 hidden sm:table-cell">
                            <div className="flex flex-col">
                              <span className={`font-medium ${record.punchIn?.time ? 'text-green-700' : 'text-gray-400'}`}>
                                {record.punchIn?.time 
                                  ? new Date(record.punchIn.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
                                  : '--:--'}
                              </span>
                              {record.punchIn?.distanceFromOffice !== undefined && (
                                <span className="text-xs text-gray-500">
                                  ({record.punchIn.distanceFromOffice >= 1000 
                                    ? `${(record.punchIn.distanceFromOffice / 1000).toFixed(2)} km` 
                                    : `${record.punchIn.distanceFromOffice}m`})
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 hidden sm:table-cell">
                            <div className="flex flex-col">
                              <span className={`font-medium ${record.punchOut?.time ? 'text-red-700' : 'text-gray-400'}`}>
                                {record.punchOut?.time 
                                  ? new Date(record.punchOut.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
                                  : '--:--'}
                              </span>
                              {record.punchOut?.distanceFromOffice !== undefined && (
                                <span className="text-xs text-gray-500">
                                  ({record.punchOut.distanceFromOffice >= 1000 
                                    ? `${(record.punchOut.distanceFromOffice / 1000).toFixed(2)} km` 
                                    : `${record.punchOut.distanceFromOffice}m`})
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 hidden md:table-cell">
                            <span className="font-medium text-blue-700">
                              {record.workDuration ? `${Math.floor(record.workDuration / 60)}h ${record.workDuration % 60}m` : '-'}
                            </span>
                          </td>
                          <td className="px-3 py-3 hidden lg:table-cell">
                            {(() => {
                              const total = record.locationLogs?.length || 0;
                              const missed = record.locationLogs?.filter(l => l.isMissed)?.length || 0;
                              const outOfRange = record.locationLogs?.filter(l => !l.isWithinRange && !l.isMissed)?.length || 0;
                              return (
                                <div className="flex items-center gap-1">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    missed > 0 ? 'bg-red-100 text-red-700' : total > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                                  }`}>
                                    {total}
                                  </span>
                                  {missed > 0 && (
                                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-red-500 text-white" title="Missed checks">
                                      {missed}❌
                                    </span>
                                  )}
                                  {outOfRange > 0 && (
                                    <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700" title="Out of range">
                                      {outOfRange}⚠
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-3 hidden lg:table-cell">
                            {(() => {
                              const failed = record.failedAttempts?.length || 0;
                              return failed > 0 ? (
                                <span className="px-2 py-1 rounded text-xs font-bold bg-red-500 text-white" title="Failed punch attempts">
                                  🚫 {failed}
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">-</span>
                              );
                            })()}
                          </td>
                          <td className="px-3 py-3 text-center">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-gray-400 inline" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-400 inline" />
                            )}
                          </td>
                        </tr>
                        
                        {/* Expanded Details Row */}
                        {isExpanded && (
                          <tr>
                            <td colSpan="8" className="px-3 py-4 bg-gray-50">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {/* Punch In Details */}
                                <div className="bg-white rounded-lg p-3 border border-green-200">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-4 h-4 text-green-600" />
                                    <span className="font-medium text-green-900 text-sm">Punch In</span>
                                    <span className="text-xs text-green-700 ml-auto">
                                      {record.punchIn?.time 
                                        ? new Date(record.punchIn.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
                                        : '--:--'}
                                    </span>
                                  </div>
                                  {record.punchIn?.photo?.url ? (
                                    <a href={record.punchIn.photo.url} target="_blank" rel="noopener noreferrer">
                                      <img 
                                        src={record.punchIn.photo.url} 
                                        alt="Punch In" 
                                        className="w-full h-24 object-cover rounded border"
                                      />
                                    </a>
                                  ) : (
                                    <div className="w-full h-24 bg-green-50 rounded flex items-center justify-center text-xs text-green-600">
                                      No photo
                                    </div>
                                  )}
                                  {record.punchIn?.distanceFromOffice !== undefined && (
                                    <p className="text-xs text-gray-500 mt-2">
                                      📍 {record.punchIn.distanceFromOffice}m from office
                                    </p>
                                  )}
                                </div>
                                
                                {/* Punch Out Details */}
                                <div className="bg-white rounded-lg p-3 border border-red-200">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-4 h-4 text-red-600" />
                                    <span className="font-medium text-red-900 text-sm">Punch Out</span>
                                    <span className="text-xs text-red-700 ml-auto">
                                      {record.punchOut?.time 
                                        ? new Date(record.punchOut.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
                                        : '--:--'}
                                    </span>
                                  </div>
                                  {record.punchOut?.photo?.url ? (
                                    <a href={record.punchOut.photo.url} target="_blank" rel="noopener noreferrer">
                                      <img 
                                        src={record.punchOut.photo.url} 
                                        alt="Punch Out" 
                                        className="w-full h-24 object-cover rounded border"
                                      />
                                    </a>
                                  ) : (
                                    <div className="w-full h-24 bg-red-50 rounded flex items-center justify-center text-xs text-red-600">
                                      {record.punchOut?.time ? 'No photo' : 'Not punched out'}
                                    </div>
                                  )}
                                  {record.punchOut?.distanceFromOffice !== undefined && (
                                    <p className="text-xs text-gray-500 mt-2">
                                      📍 {record.punchOut.distanceFromOffice}m from office
                                    </p>
                                  )}
                                </div>
                                
                                {/* Additional Info */}
                                <div className="bg-white rounded-lg p-3 border border-gray-200">
                                  <div className="flex items-center gap-2 mb-2">
                                    <FileText className="w-4 h-4 text-gray-600" />
                                    <span className="font-medium text-gray-900 text-sm">Details</span>
                                  </div>
                                  <div className="space-y-2 text-xs">
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Work Duration</span>
                                      <span className="font-medium">{formatWorkDuration(record.workDuration)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Location Checks</span>
                                      <span className="font-medium">{record.locationLogs?.length || 0}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Late Arrival</span>
                                      <span className={`font-medium ${record.isLateArrival ? 'text-orange-600' : 'text-green-600'}`}>
                                        {record.isLateArrival ? 'Yes' : 'No'}
                                      </span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Early Departure</span>
                                      <span className={`font-medium ${record.isEarlyDeparture ? 'text-orange-600' : 'text-green-600'}`}>
                                        {record.isEarlyDeparture ? 'Yes' : 'No'}
                                      </span>
                                    </div>
                                    {record.notes && (
                                      <div className="pt-2 border-t">
                                        <span className="text-gray-500">Notes: </span>
                                        <span>{record.notes}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Location Check Logs - HIGH PRIORITY SECTION */}
                              {record.locationLogs?.length > 0 && (
                                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <div className="flex items-center justify-between mb-3">
                                    <p className="text-sm font-medium text-blue-900">
                                      📍 Location Checks ({record.locationLogs.length})
                                    </p>
                                    <div className="flex gap-2 text-xs">
                                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                        ✓ {record.locationLogs.filter(l => l.isWithinRange && !l.isMissed).length} OK
                                      </span>
                                      <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                                        ⚠ {record.locationLogs.filter(l => !l.isWithinRange && !l.isMissed).length} Out of Range
                                      </span>
                                      {record.locationLogs.filter(l => l.isMissed).length > 0 && (
                                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-medium">
                                          ❌ {record.locationLogs.filter(l => l.isMissed).length} Missed
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {/* Show missed checks first with high priority */}
                                    {[...record.locationLogs]
                                      .sort((a, b) => {
                                        // Missed checks first, then by timestamp
                                        if (a.isMissed && !b.isMissed) return -1;
                                        if (!a.isMissed && b.isMissed) return 1;
                                        return new Date(a.timestamp) - new Date(b.timestamp);
                                      })
                                      .map((log, idx) => (
                                        <div 
                                          key={idx} 
                                          className={`flex items-center gap-3 p-2 rounded text-xs ${
                                            log.isMissed 
                                              ? 'bg-red-100 border border-red-300'
                                              : log.isWithinRange 
                                                ? 'bg-green-50' 
                                                : 'bg-orange-50'
                                          }`}
                                        >
                                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                            log.isMissed 
                                              ? 'bg-red-500'
                                              : log.type === 'punch_in' 
                                                ? 'bg-green-600' 
                                                : log.type === 'punch_out' 
                                                  ? 'bg-red-600' 
                                                  : log.isWithinRange 
                                                    ? 'bg-blue-500' 
                                                    : 'bg-orange-500'
                                          }`}>
                                            {log.isMissed ? '!' : idx + 1}
                                          </div>
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium">
                                                {new Date(log.timestamp).toLocaleTimeString('en-IN', { 
                                                  hour: '2-digit', 
                                                  minute: '2-digit',
                                                  hour12: true,
                                                  timeZone: 'Asia/Kolkata' 
                                                })}
                                              </span>
                                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                                log.isMissed 
                                                  ? 'bg-red-200 text-red-800'
                                                  : log.type === 'punch_in' 
                                                    ? 'bg-green-200 text-green-800' 
                                                    : log.type === 'punch_out' 
                                                      ? 'bg-red-200 text-red-800' 
                                                      : log.isWithinRange 
                                                        ? 'bg-blue-200 text-blue-800' 
                                                        : 'bg-orange-200 text-orange-800'
                                              }`}>
                                                {log.isMissed 
                                                  ? '❌ MISSED' 
                                                  : log.type === 'punch_in' 
                                                    ? '🟢 Punch In' 
                                                    : log.type === 'punch_out' 
                                                      ? '🔴 Punch Out' 
                                                      : log.isWithinRange 
                                                        ? '📍 Check' 
                                                        : '⚠ Out of Range'}
                                              </span>
                                            </div>
                                            {log.isMissed && log.failureReason && (
                                              <p className="text-red-700 mt-1 font-medium">
                                                Reason: {log.failureReason}
                                              </p>
                                            )}
                                            {log.isMissed && log.expectedTime && (
                                              <p className="text-red-600 mt-0.5">
                                                Expected at: {new Date(log.expectedTime).toLocaleTimeString('en-IN', { 
                                                  hour: '2-digit', 
                                                  minute: '2-digit',
                                                  hour12: true,
                                                  timeZone: 'Asia/Kolkata' 
                                                })}
                                              </p>
                                            )}
                                            {!log.isMissed && log.distanceFromOffice !== undefined && (
                                              <span className={`${log.isWithinRange ? 'text-green-600' : 'text-orange-600'}`}>
                                                {log.distanceFromOffice >= 1000 
                                                  ? `${(log.distanceFromOffice / 1000).toFixed(1)} km` 
                                                  : `${log.distanceFromOffice}m`} from office
                                              </span>
                                            )}
                                          </div>
                                          {!log.isMissed && log.latitude && log.longitude && (
                                            <a 
                                              href={`https://www.google.com/maps?q=${log.latitude},${log.longitude}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:underline"
                                            >
                                              📍 Map
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Violations */}
                              {record.violations?.length > 0 && (
                                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                  <p className="text-xs font-medium text-orange-900 mb-2">⚠️ Violations ({record.violations.length})</p>
                                  <div className="space-y-1">
                                    {record.violations.map((v, i) => (
                                      <p key={i} className="text-xs text-orange-700">{v.description}</p>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Failed Punch Attempts */}
                              {record.failedAttempts?.length > 0 && (
                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <p className="text-sm font-medium text-red-900 mb-3">🚫 Failed Punch Attempts ({record.failedAttempts.length})</p>
                                  <div className="space-y-3 max-h-60 overflow-y-auto">
                                    {record.failedAttempts.map((attempt, i) => (
                                      <div key={i} className="bg-white p-3 rounded border border-red-100">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                            attempt.type === 'punch_in' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                          }`}>
                                            {attempt.type === 'punch_in' ? '🟢 Punch In Attempt' : '🔴 Punch Out Attempt'}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            {new Date(attempt.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                                          </span>
                                        </div>
                                        <p className="text-sm text-red-700 font-medium mb-2">
                                          ❌ {attempt.reason}
                                        </p>
                                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                          <div>
                                            <span className="text-gray-400">Distance: </span>
                                            <span className="font-medium text-red-600">
                                              {attempt.distanceFromOffice >= 1000 
                                                ? `${(attempt.distanceFromOffice / 1000).toFixed(2)} km` 
                                                : `${attempt.distanceFromOffice}m`}
                                            </span>
                                          </div>
                                          {attempt.location?.latitude && (
                                            <div>
                                              <a 
                                                href={`https://www.google.com/maps?q=${attempt.location.latitude},${attempt.location.longitude}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:underline"
                                              >
                                                📍 View Location
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                        {attempt.photo?.url && (
                                          <div className="mt-2">
                                            <a href={attempt.photo.url} target="_blank" rel="noopener noreferrer">
                                              <img 
                                                src={attempt.photo.url} 
                                                alt="Failed attempt photo" 
                                                className="w-20 h-20 object-cover rounded border border-red-200"
                                              />
                                            </a>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Attendance Records</h3>
              <p className="text-gray-500">No attendance records found for {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'leaves' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Leave History</h3>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {[2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {leavesLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : leaves.length > 0 ? (
            <div className="space-y-4">
              {leaves.map((leave) => (
                <div key={leave._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900 capitalize">{leave.leaveType?.replace('_', ' ')} Leave</h4>
                        <Badge className={getLeaveStatusColor(leave.status)}>
                          {leave.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500">
                        {formatDate(leave.startDate)} - {formatDate(leave.endDate)} ({leave.totalDays} day{leave.totalDays > 1 ? 's' : ''})
                      </p>
                      {leave.reason && (
                        <p className="text-sm text-gray-600 mt-2">{leave.reason}</p>
                      )}
                    </div>
                  </div>
                  {leave.adminResponse && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-sm text-gray-500">
                        <span className="font-medium">Admin Response:</span> {leave.adminResponse}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No leave records for {selectedYear}</p>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'payroll' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Payroll History</h3>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {[2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {payrollLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : payslips.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Month</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Basic</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Allowances</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Deductions</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Net Pay</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payslips.map((payslip) => (
                    <tr key={payslip._id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-medium">
                        {months[payslip.month - 1]} {payslip.year}
                      </td>
                      <td className="py-3 px-4 text-sm">₹{payslip.basicSalary?.toLocaleString() || 0}</td>
                      <td className="py-3 px-4 text-sm text-green-600">+₹{payslip.totalAllowances?.toLocaleString() || 0}</td>
                      <td className="py-3 px-4 text-sm text-red-600">-₹{payslip.totalDeductions?.toLocaleString() || 0}</td>
                      <td className="py-3 px-4 text-sm font-bold">₹{payslip.netSalary?.toLocaleString() || 0}</td>
                      <td className="py-3 px-4">
                        <Badge className={payslip.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {payslip.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        {payslip.pdfUrl && (
                          <a 
                            href={payslip.pdfUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm"
                          >
                            View PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Wallet className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No payroll records for {selectedYear}</p>
            </div>
          )}
        </Card>
      )}

      {/* Travel Map Tab - Only for Field Employees */}
      {activeTab === 'travelmap' && employee.locationType === 'field' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Travel Map</h3>
                <p className="text-sm text-gray-500">Track field employee movement throughout the day</p>
              </div>
            </div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {dailyLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : attendance && attendance.locationLogs?.length > 0 ? (
            <div className="space-y-6">
              {/* Map iframe using Google Maps Static API */}
              <div className="bg-gray-100 rounded-xl overflow-hidden" style={{ height: '400px' }}>
                {/* Create a path URL for Google Maps */}
                {(() => {
                  const logs = attendance.locationLogs;
                  const pathCoords = logs.map(l => `${l.latitude},${l.longitude}`).join('|');
                  const markers = logs.map((l, idx) => 
                    `&markers=color:${l.isWithinRange ? 'green' : 'red'}%7Clabel:${idx + 1}%7C${l.latitude},${l.longitude}`
                  ).join('');
                  const center = logs[Math.floor(logs.length / 2)];
                  
                  return (
                    <iframe
                      title="Travel Map"
                      width="100%"
                      height="400"
                      style={{ border: 0 }}
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed/v1/view?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&center=${center.latitude},${center.longitude}&zoom=14`}
                    />
                  );
                })()}
              </div>

              {/* Travel Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{attendance.locationLogs.length}</p>
                  <p className="text-sm text-blue-600">Total Check-ins</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {attendance.locationLogs.filter(l => l.isWithinRange).length}
                  </p>
                  <p className="text-sm text-green-600">At Client/Office</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-700">
                    {attendance.locationLogs.filter(l => !l.isWithinRange).length}
                  </p>
                  <p className="text-sm text-orange-600">In Transit</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700">
                    {formatWorkDuration(attendance.workDuration)}
                  </p>
                  <p className="text-sm text-purple-600">Work Hours</p>
                </div>
              </div>

              {/* Location Timeline with Google Maps Links */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-medium text-gray-900 mb-4">📍 Location Timeline</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {attendance.locationLogs.map((log, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-start gap-4 p-4 rounded-lg border-l-4 ${
                        log.isWithinRange 
                          ? 'bg-green-50 border-green-500' 
                          : 'bg-orange-50 border-orange-500'
                      }`}
                    >
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                          log.isWithinRange ? 'bg-green-500' : 'bg-orange-500'
                        }`}>
                          {idx + 1}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-gray-900">
                            {new Date(log.timestamp).toLocaleTimeString('en-IN', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: true,
                              timeZone: 'Asia/Kolkata' 
                            })}
                          </p>
                          <Badge className={log.isWithinRange ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}>
                            {log.type === 'punch_in' ? '🟢 Punch In' : log.type === 'punch_out' ? '🔴 Punch Out' : log.isWithinRange ? '📍 At Location' : '🚗 Traveling'}
                          </Badge>
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-gray-600">
                          <div>
                            <span className="text-gray-400">Coordinates:</span> {log.latitude?.toFixed(5)}, {log.longitude?.toFixed(5)}
                          </div>
                          {log.distanceFromOffice !== undefined && (
                            <div>
                              <span className="text-gray-400">Distance:</span>{' '}
                              <span className={log.isWithinRange ? 'text-green-700' : 'text-orange-700'}>
                                {log.distanceFromOffice >= 1000 
                                  ? `${(log.distanceFromOffice / 1000).toFixed(1)} km` 
                                  : `${Math.round(log.distanceFromOffice)} m`}
                              </span>
                            </div>
                          )}
                        </div>
                        <a 
                          href={`https://www.google.com/maps?q=${log.latitude},${log.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-2"
                        >
                          <MapPin className="w-3 h-3" />
                          Open in Google Maps
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Punch Photos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Punch In Photo */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-green-600" />
                    <h4 className="font-medium text-green-900">Punch In Photo</h4>
                    <span className="text-sm text-green-700 ml-auto">{formatTime(attendance.punchIn?.time)}</span>
                  </div>
                  {attendance.punchIn?.photo?.url ? (
                    <div className="relative">
                      <img 
                        src={attendance.punchIn.photo.url} 
                        alt="Punch In" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-green-100 rounded-lg flex items-center justify-center">
                      <p className="text-sm text-green-600">No photo</p>
                    </div>
                  )}
                </div>

                {/* Punch Out Photo */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4 text-red-600" />
                    <h4 className="font-medium text-red-900">Punch Out Photo</h4>
                    <span className="text-sm text-red-700 ml-auto">{formatTime(attendance.punchOut?.time)}</span>
                  </div>
                  {attendance.punchOut?.photo?.url ? (
                    <div className="relative">
                      <img 
                        src={attendance.punchOut.photo.url} 
                        alt="Punch Out" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-48 bg-red-100 rounded-lg flex items-center justify-center">
                      <p className="text-sm text-red-600">{attendance.punchOut?.time ? 'No photo' : 'Not punched out'}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Attendance Record</h3>
              <p className="text-gray-500 mb-2">This employee did not punch in on {new Date(selectedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              <Badge className="bg-red-100 text-red-700">❌ Marked as Absent</Badge>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default EmployeeDetailPage;
